/**
 * Provider compatibility utilities for handling different AI provider formats
 */

/**
 * Convert OpenAI Assistant tools to chat completion tools format
 * @param {Array} assistantTools - Tools from the OpenAI Assistant
 * @returns {Array} - Tools formatted for chat completion API
 */
export function convertAssistantTools(assistantTools) {
  if (!assistantTools || !Array.isArray(assistantTools)) return [];

  return assistantTools
    .map((tool) => {
      if (tool.type === "function") {
        // Function tool - copy as is but ensure correct structure
        return {
          type: "function",
          function: {
            name: tool.function.name,
            description: tool.function.description,
            parameters: tool.function.parameters,
          },
        };
      } else if (tool.type === "code_interpreter") {
        // Code interpreter doesn't translate directly to chat completion
        // We'll implement a simplified version
        return {
          type: "function",
          function: {
            name: "run_code",
            description: "Run code in a secure environment",
            parameters: {
              type: "object",
              properties: {
                language: {
                  type: "string",
                  description: "The programming language to use",
                  enum: [
                    "python",
                    "javascript",
                    "typescript",
                    "shell",
                    "html",
                    "css",
                  ],
                },
                code: {
                  type: "string",
                  description: "The code to execute",
                },
              },
              required: ["language", "code"],
            },
          },
        };
      } else if (tool.type === "retrieval") {
        // Retrieval doesn't directly map to chat completion
        // This is a placeholder for document lookup functionality
        return {
          type: "function",
          function: {
            name: "search_documents",
            description: "Search through available documents",
            parameters: {
              type: "object",
              properties: {
                query: {
                  type: "string",
                  description: "The search query",
                },
              },
              required: ["query"],
            },
          },
        };
      } else {
        // For unsupported tool types, return null
        return null;
      }
    })
    .filter(Boolean); // Remove any null values
}

/**
 * Process message roles for provider compatibility
 * @param {Array} messages - Original message array
 * @param {string} providerName - Provider name for compatibility
 * @param {string} modelName - Model name for specific model handling
 * @returns {Array} - Processed message array with appropriate role formats
 */
export function ensureProviderMessageCompatibility(
  messages,
  providerName,
  modelName
) {
  if (!messages || !Array.isArray(messages)) {
    return [];
  }

  const lowerProvider = (providerName || "").toLowerCase();
  const lowerModel = (modelName || "").toLowerCase();

  // For DeepSeek reasoner models, we let the provider handle format
  // as it has special duplicate message filtering and alternation requirements
  if (
    lowerProvider === "deepseek" &&
    (lowerModel.includes("deepseek-reason") ||
      lowerModel.includes("deepseek-r1") ||
      lowerModel.includes("deepseek-v2") ||
      lowerModel.includes("deepseek-v3"))
  ) {
    return messages;
  }

  // Since we're now using backend providers exclusively, the backend handles
  // most provider-specific formatting. However, we still need to ensure
  // proper message structure for the frontend.

  // For Anthropic backend provider, ensure tool messages have proper structure
  if (lowerProvider === "anthropic") {
    return formatAnthropicMessages(messages);
  } else if (lowerProvider === "cohere") {
    return formatCohereMessages(messages);
  } else if (lowerProvider === "google" || lowerProvider === "vertex") {
    return formatGoogleMessages(messages);
  } else if (lowerProvider === "mistral") {
    return formatMistralMessages(messages);
  } else if (lowerProvider === "perplexity") {
    return formatPerplexityMessages(messages);
  }

  // Default: return messages as-is for OpenAI and compatible providers
  return messages;
}

/**
 * Format messages for Anthropic Claude API (Backend Compatible)
 * @param {Array} messages - Messages to format
 * @returns {Array} - Formatted messages for Anthropic backend
 */
function formatAnthropicMessages(messages) {
  // The backend now handles Anthropic format conversion, but we still need
  // to ensure proper tool message structure for the frontend
  return messages.map((message) => {
    // Handle tool messages specifically
    if (message.role === "tool") {
      // Ensure tool_call_id is present and properly formatted
      const toolCallId =
        message.tool_call_id ||
        (message.tool && message.tool.tool_call_id) ||
        `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

      // Format with flat structure for backend compatibility
      const formattedToolMessage = {
        role: "tool",
        tool_call_id: toolCallId,
        content: message.content || "No result",
      };

      console.log("🔧 [ANTHROPIC-COMPAT] Formatted tool message:", {
        originalId: message.tool_call_id,
        formattedId: toolCallId,
        hasContent: !!message.content,
      });

      return formattedToolMessage;
    }

    // Handle assistant messages with tool calls
    if (message.role === "assistant" && message.tool_calls) {
      return {
        ...message,
        tool_calls: message.tool_calls.map((tc) => ({
          id:
            tc.id ||
            `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
          type: tc.type || "function",
          function: {
            name: tc.function?.name || "unknown_function",
            arguments: tc.function?.arguments || "{}",
          },
        })),
      };
    }

    return message;
  });
}

/**
 * Format messages for Cohere API
 * @param {Array} messages - Messages to format
 * @returns {Array} - Formatted messages for Cohere
 */
function formatCohereMessages(messages) {
  // Cohere's API has special requirements for handling system messages
  const formattedMessages = [];
  let pendingSystemContent = "";

  // First pass: collect system messages and add other types
  for (const message of messages) {
    if (message.role === "system") {
      // Collect system message content
      pendingSystemContent +=
        (pendingSystemContent ? "\n" : "") + message.content;
    } else {
      // Add other message types directly
      formattedMessages.push(message);
    }
  }

  // Insert combined system message at start if any exist
  if (pendingSystemContent) {
    // Cohere requires system messages as preamble in user messages
    // rather than as separate message types
    if (formattedMessages.length > 0 && formattedMessages[0].role === "user") {
      // Prepend to first user message
      formattedMessages[0].content = `${pendingSystemContent}\n\n${formattedMessages[0].content}`;
    } else {
      // Add as new user message if no user message exists
      formattedMessages.unshift({
        role: "user",
        content: pendingSystemContent,
      });
    }
  }

  return formattedMessages;
}

/**
 * Format messages for Google Vertex AI API
 * @param {Array} messages - Messages to format
 * @returns {Array} - Formatted messages for Google
 */
function formatGoogleMessages(messages) {
  // Google's API expects specific role names
  return messages.map((msg) => {
    // Clone to avoid mutating the original
    const formattedMsg = { ...msg };

    // Convert roles to Google's expected format
    if (msg.role === "user") {
      formattedMsg.role = "user";
    } else if (msg.role === "assistant") {
      formattedMsg.role = "model";
    } else if (msg.role === "system") {
      formattedMsg.role = "system";
    }

    return formattedMsg;
  });
}

/**
 * Format messages for Mistral API
 * @param {Array} messages - Messages to format
 * @returns {Array} - Formatted messages for Mistral
 */
function formatMistralMessages(messages) {
  // Mistral API is OpenAI-compatible, but may have specific requirements
  return messages;
}

/**
 * Format messages for Perplexity API
 * @param {Array} messages - Messages to format
 * @returns {Array} - Formatted messages for Perplexity
 */
function formatPerplexityMessages(messages) {
  // Handle special cases for Perplexity API
  const formattedMessages = [];
  let systemContent = "";

  // Collect system message content
  for (const message of messages) {
    if (message.role === "system") {
      systemContent += (systemContent ? "\n" : "") + message.content;
    } else {
      formattedMessages.push(message);
    }
  }

  // Add system message at the beginning if we collected any
  if (systemContent) {
    formattedMessages.unshift({
      role: "system",
      content: systemContent,
    });
  }

  return formattedMessages;
}

/**
 * Sanitize messages to prevent function_call and tool_calls from appearing in the same message
 * @param {Array} messages - Messages to sanitize
 * @returns {Array} - Sanitized messages
 */
export function sanitizeMessages(messages) {
  if (!messages || !Array.isArray(messages)) return messages;

  // First, deduplicate and clean up tool calls with the same ID
  let messagesWithCleanToolCalls = messages.map((message) => {
    // Skip non-objects or null
    if (!message || typeof message !== "object") return message;

    // Create a deep copy to avoid mutating the original
    const sanitizedMessage = { ...message };

    // Check if both function_call and tool_calls exist on the same message
    if (sanitizedMessage.function_call && sanitizedMessage.tool_calls) {
      // Prefer tool_calls (newer format) over function_call (older format)
      delete sanitizedMessage.function_call;
    }

    // If this message has tool calls, deduplicate them by ID and keep only complete ones
    if (
      sanitizedMessage.tool_calls &&
      Array.isArray(sanitizedMessage.tool_calls)
    ) {
      // Create a map to keep the most complete version of each tool call by ID
      const toolCallsById = new Map();

      // Process all tool calls, keeping the most complete one for each ID
      sanitizedMessage.tool_calls.forEach((toolCall) => {
        if (!toolCall || !toolCall.id) return;

        // If we've already seen this ID, compare completeness
        if (toolCallsById.has(toolCall.id)) {
          const existing = toolCallsById.get(toolCall.id);

          // Determine which is more complete
          const existingArgsLength = existing.function?.arguments?.length || 0;
          const newArgsLength = toolCall.function?.arguments?.length || 0;

          // Only replace if the new one is more complete
          if (newArgsLength > existingArgsLength) {
            // Ensure we're getting the complete arguments instead of a truncated version
            toolCallsById.set(
              toolCall.id,
              JSON.parse(JSON.stringify(toolCall))
            );
          }
        } else {
          // First time seeing this ID - make a deep copy to ensure we don't lose data
          toolCallsById.set(toolCall.id, JSON.parse(JSON.stringify(toolCall)));
        }
      });

      // Now filter to keep only complete tool calls (not marked as streaming and have valid args)
      const completeToolCalls = Array.from(toolCallsById.values()).filter(
        (toolCall) => {
          // Skip tool calls still marked as streaming
          if (toolCall.isStreaming) return false;

          // Check for empty or invalid arguments
          const args = toolCall.function?.arguments;
          if (!args || args === "{}" || !args.includes('":')) return false;

          return true;
        }
      );

      // Update the message's tool_calls with our filtered list
      sanitizedMessage.tool_calls =
        completeToolCalls.length > 0 ? completeToolCalls : undefined;
    }

    return sanitizedMessage;
  });

  // CRITICAL FIX FOR OPENAI ERROR: Ensure each 'tool' message has a corresponding previous message with tool_calls
  let validMessages = [];
  let toolCallIds = new Set(); // Track all tool call IDs from assistant messages
  let knownToolNames = new Set(); // Track tool names for fallback matching

  // First pass: collect all tool call IDs from assistant messages
  messagesWithCleanToolCalls.forEach((message) => {
    if (
      message.role === "assistant" &&
      message.tool_calls &&
      Array.isArray(message.tool_calls)
    ) {
      message.tool_calls.forEach((tc) => {
        if (tc && tc.id) {
          toolCallIds.add(tc.id);
          // Also store the tool name for fallback matching
          if (tc.function && tc.function.name) {
            knownToolNames.add(tc.function.name);
          }
        }
      });
    }

    // Also check for tool call IDs in function_call (older format)
    if (message.role === "assistant" && message.function_call) {
      const fnName = message.function_call.name;
      if (fnName) {
        knownToolNames.add(fnName);
        // Generate a synthetic ID for backward compatibility
        toolCallIds.add(`fn_${fnName}_${Date.now()}`);
      }
    }
  });

  // Second pass: process tool messages with improved validation
  for (let i = 0; i < messagesWithCleanToolCalls.length; i++) {
    const message = messagesWithCleanToolCalls[i];

    // Skip invalid messages
    if (!message) continue;

    // For tool messages, validate they have a tool_call_id or at least a valid name
    if (message.role === "tool" || message.role === "function") {
      let isValid = false;

      // Check if tool_call_id exists and matches a known ID
      if (message.tool_call_id && toolCallIds.has(message.tool_call_id)) {
        isValid = true;
      }
      // Fallback: Check if the tool name is known even without a matching ID
      else if (message.name && knownToolNames.has(message.name)) {
        isValid = true;
        // Generate a synthetic ID if missing
        if (!message.tool_call_id) {
          message.tool_call_id = `tc_fix_${message.name}_${Date.now()}`;
        }
      }

      // Skip invalid tool messages only if we have strict validation enabled
      // For now, allow all tool messages to pass through for resilience in streaming
      const strictValidation = false; // Set to true to enforce strict validation

      if (!isValid && strictValidation) {
        // For now, try to recover by adding a synthetic ID if possible
        if (message.name) {
          message.tool_call_id = `tc_recovered_${message.name}_${Date.now()}`;
          isValid = true;
        } else {
          // Skip truly invalid messages
          continue;
        }
      }
    }

    // Add message to final array
    validMessages.push(message);
  }

  // Next, handle duplicate assistant messages with identical tool calls
  // For OpenAI streaming, we often get many versions of the same message
  const seenToolCallIdCombos = new Set();
  const uniqueMessages = validMessages.filter((message) => {
    // Keep all non-assistant messages
    if (message?.role !== "assistant") return true;

    // Keep assistant messages without tool calls
    if (!message.tool_calls || !Array.isArray(message.tool_calls)) return true;

    // For messages with tool calls, deduplicate by the set of tool call IDs
    const toolCallIds = message.tool_calls
      .map((tc) => tc.id)
      .sort()
      .join(",");

    // Skip if we've seen this exact combination before
    if (toolCallIds && seenToolCallIdCombos.has(toolCallIds)) {
      return false;
    }

    // Otherwise keep this message and record the IDs
    seenToolCallIdCombos.add(toolCallIds);
    return true;
  });

  return uniqueMessages;
}
